/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService, LogService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { LockModel } from '../models';
import { LocksEnum } from '../enums/api';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';

@Injectable()
export class LockApiService {

    /**
     * Polling counter for lock status.
     * @private
     */
    private pollingCounter: number = 0;

    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService,
        private logService: LogService
    ) {
    }

    /**
     * Get API locks status
     * @returns {Observable<any>}
     */
    public getLockStatus(options?: any): Observable<any> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(
                    LocksEnum.GET_LOCK_STATUS,
                    moduleAuth
                ).subscribe(
                    (response: LockModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get API locks status
     * @returns {Observable<any>}
     */
    public unLockUserLock(options?: any): Observable<any> {
        return new Observable(
            (observer) => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(
                    LocksEnum.DELETE_LOCK_USER,
                    moduleAuth
                ).subscribe(
                    (response: LockModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Poll for lock status.
     */
    public pollForLockStatus(): Observable<any> {
        return new Observable(
            (observer) => {
                this.pollingCounter = 0;
                this.getStatusHelper(observer);
            }
        );
    }

    /**
     * Get status helper.
     * @param observer
     * @private
     */
    private getStatusHelper(observer: any): void {
        if (this.pollingCounter < 4) {
            this.getLockStatus()
                .subscribe({
                    next: (response: any): void => {
                        this.checkApiLockState(observer, response);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('LockApiService', 'Unable to get lock: ' + error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        } else {
            const errorMessage = 'API lock status poller, reached maximum count.';
            this.logService.error('LockApiService', errorMessage);
            observer.error(new Error(errorMessage));
            observer.complete();
        }
    }

    /**
     * Check API Lock State.
     * @param observer
     * @param response
     * @private
     */
    private checkApiLockState(observer: any, response: { APILock: any; }): void {
        if (response.APILock) {
            const failedTimeout: any = setTimeout((): void => {
                this.pollingCounter++;
                this.getStatusHelper(observer);
                clearTimeout(failedTimeout);
            }, 2500);
        } else if (!response.APILock) {
            this.logService.error('LockApiService:checkApiLockState()', 'Rollback complete');

            observer.next();
            observer.complete();
        }
    }
}
